import ApplicationController from "application_controller.js";
import * as Turbo from "@hotwired/turbo";

//* Makes a Bootstrap offcanvas Turbo-aware. Attach directly on the .offcanvas element.
//* - Slides the offcanvas away (instead of vanishing) before navigating while it is open
//* - Keeps the open state out of Turbo's cache so it never reappears on the back button
//* - Slides the offcanvas away when one of its links jumps to a section of the current page
//* Takes the Offcanvas class of the bundle, because a bundle must not mix the full "bootstrap" with "bootstrap/js/dist/*".
//* Import index.js in bundles with the full "bootstrap" and dist.js in bundles that import plugins one by one.
//*   <div class="offcanvas offcanvas-end" id="nav" tabindex="-1" data-controller="offcanvas">...</div>
export default (Offcanvas) => class extends ApplicationController {
  debug = false;

  connect() {
    this.log_function("connect");

    this.boundSlideAwayBeforeVisit = this.slideAwayBeforeVisit.bind(this);
    this.boundCleanupBeforeCache = this.cleanupBeforeCache.bind(this);
    this.boundSlideAwayOnSectionLink = this.slideAwayOnSectionLink.bind(this);

    document.addEventListener("turbo:before-visit", this.boundSlideAwayBeforeVisit);
    document.addEventListener("turbo:before-cache", this.boundCleanupBeforeCache);
    this.element.addEventListener("click", this.boundSlideAwayOnSectionLink);
  }

  disconnect() {
    this.log_function("disconnect");

    document.removeEventListener("turbo:before-visit", this.boundSlideAwayBeforeVisit);
    document.removeEventListener("turbo:before-cache", this.boundCleanupBeforeCache);
    this.element.removeEventListener("click", this.boundSlideAwayOnSectionLink);
  }

  //* "show" persists while Bootstrap plays the hiding transition, "showing" while opening
  get visible() {
    return this.element.classList.contains("show") || this.element.classList.contains("showing");
  }

  //* Hold the visit until the slide-away transition finishes, then re-issue it
  slideAwayBeforeVisit(event) {
    if (!this.visible) return;

    this.log_function("slideAwayBeforeVisit", event.detail.url);
    event.preventDefault();
    this.pendingUrl = event.detail.url;

    if (this.sliding) return; //* already sliding away; just update the pending visit's destination
    this.sliding = true;

    const proceed = () => {
      if (!this.sliding) return;
      this.sliding = false;
      clearTimeout(this.slideFallback);
      Turbo.visit(this.pendingUrl);
    };

    this.element.addEventListener("hidden.bs.offcanvas", proceed, { once: true });
    this.slideFallback = setTimeout(proceed, 700); //* never lose the navigation if the transition dies
    Offcanvas.getOrCreateInstance(this.element).hide();
  }

  //* Hold a jump to a section of the current page until the slide-away finishes, then let the browser jump.
  //* Turbo snapshots the page on that jump, so the offcanvas must be closed by then.
  slideAwayOnSectionLink(event) {
    const link = event.target.closest("a[href]");
    if (!link || !link.hash || link.pathname + link.search !== location.pathname + location.search) return;

    this.log_function("slideAwayOnSectionLink", link.hash);
    event.preventDefault();
    this.element.addEventListener("hidden.bs.offcanvas", () => location.assign(link.href), { once: true });
    Offcanvas.getOrCreateInstance(this.element).hide();
  }

  //* Snapshot a fully closed offcanvas so restoring from cache (back button) never shows it open
  cleanupBeforeCache() {
    this.log_function("cleanupBeforeCache");

    Offcanvas.getInstance(this.element)?.dispose();
    this.element.classList.remove("show", "showing", "hiding");
    this.element.removeAttribute("aria-modal");
    this.element.removeAttribute("role");
    document.querySelectorAll(".offcanvas-backdrop").forEach((backdrop) => backdrop.remove());
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
  }
};
